import { detectPlatform, type EctApiClient } from "@ect/core";

/**
 * Device push registration (PRD M13-01). Android app: Firebase Cloud Messaging through Capacitor; browsers and the iOS
 * home-screen app: Web Push (VAPID) through the service worker; ECT Desk: nothing here — the desktop shows OS toasts from
 * the synced notifications table (see notifications-os.tsx). Returns the state for the settings UI.
 */
export type PushState = "unsupported" | "denied" | "prompt" | "granted";

export async function pushState(): Promise<PushState> {
  const platform = detectPlatform();
  if (platform === "tauri") return "granted"; // OS toasts, no registration needed
  if (platform === "capacitor") {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const s = (await PushNotifications.checkPermissions()).receive;
    return s === "granted" ? "granted" : s === "denied" ? "denied" : "prompt";
  }
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  return Notification.permission === "granted" ? "granted" : Notification.permission === "denied" ? "denied" : "prompt";
}

/** Ask permission if needed, obtain a token/subscription and register it for this device. */
export async function enablePush(api: EctApiClient, onOpen?: (data: Record<string, string>) => void): Promise<PushState> {
  const platform = detectPlatform();
  if (platform === "tauri") return "granted";
  if (platform === "capacitor") {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return perm.receive === "denied" ? "denied" : "prompt";
    await PushNotifications.removeAllListeners();
    let complete!: () => void;
    let fail!: (error: Error) => void;
    const registered = new Promise<void>((resolve, reject) => { complete = resolve; fail = reject; });
    // The native token callback can arrive before register() returns.
    void registered.catch(() => undefined);
    const timeout = setTimeout(() => fail(new Error("Push registration timed out")), 25_000);
    await PushNotifications.addListener("registration", (t) => {
      void api.json<void>("/api/notifications/push-token", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ token: t.value, platform: "fcm" }) }).then(() => complete(), () => fail(new Error("Could not save the push device")));
    });
    await PushNotifications.addListener("registrationError", () => fail(new Error("Push registration failed")));
    // foreground: FCM does not show the tray notification itself → show a local one so the chef notices
    await PushNotifications.addListener("pushNotificationReceived", async (n) => {
      try {
        const { LocalNotifications } = await import("@capacitor/local-notifications");
        await LocalNotifications.schedule({ notifications: [{ id: Math.floor(Math.random() * 2_000_000_000), title: n.title ?? "ECT", body: n.body ?? "", extra: n.data, channelId: (n.data as Record<string, string> | undefined)?.type === "need_blocking" ? "ect_urgent" : "ect_default" }] });
      } catch (e) { console.warn("[push] local notification failed", e); }
    });
    await PushNotifications.addListener("pushNotificationActionPerformed", (a) => onOpen?.((a.notification.data ?? {}) as Record<string, string>));
    try {
      const { LocalNotifications } = await import("@capacitor/local-notifications");
      await LocalNotifications.createChannel({ id: "ect_urgent", name: "Manques bloquants", description: "Chantier à l'arrêt", importance: 5, sound: "default", vibration: true, visibility: 1 });
      await LocalNotifications.createChannel({ id: "ect_default", name: "ECT Suite", description: "Manques, achats, rapports, missions", importance: 4, visibility: 1 });
      await LocalNotifications.addListener("localNotificationActionPerformed", (a) => onOpen?.((a.notification.extra ?? {}) as Record<string, string>));
    } catch { /* channels exist */ }
    try { await PushNotifications.register(); await registered; }
    finally { clearTimeout(timeout); }
    return "granted";
  }
  // Web Push
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "prompt";
  const cfg = await api.json<{ vapidPublicKey: string | null }>("/api/notifications/push-config");
  if (!cfg.vapidPublicKey) return "unsupported";
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.vapidPublicKey) }));
  await api.json<void>("/api/notifications/push-token", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ token: JSON.stringify(sub.toJSON()), platform: "webpush" }) });
  return "granted";
}

export async function disablePush(api: EctApiClient): Promise<void> {
  const platform = detectPlatform();
  await api.json<void>("/api/notifications/push-token", { method: "DELETE" });
  if (platform === "capacitor") { const { PushNotifications } = await import("@capacitor/push-notifications"); await PushNotifications.unregister().catch(() => undefined); }
  else if (platform === "web" && "serviceWorker" in navigator) { const reg = await navigator.serviceWorker.ready; await (await reg.pushManager.getSubscription())?.unsubscribe(); }
}

/** Silent re-registration at start when permission was already granted (tokens rotate). */
export async function refreshPushIfGranted(api: EctApiClient, onOpen?: (data: Record<string, string>) => void): Promise<void> {
  if ((await pushState()) === "granted") await enablePush(api, onOpen).catch((e) => console.warn("[push] refresh failed", e));
}

function urlBase64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
